import { createContext, useContext } from 'react'
import type { ThemeName } from '@bizfirst/common-js'

/**
 * Theme Context Type
 */
export interface ThemeContextValue {
  theme: ThemeName
  setTheme: (theme: ThemeName) => void
  toggleTheme: () => void
  isDark: boolean
  isLight: boolean
}

/**
 * Theme Context
 */
export const ThemeContext = createContext<ThemeContextValue | undefined>(undefined)

/**
 * useTheme Hook
 *
 * Access theme context from any React component
 *
 * @throws Error if used outside ThemeProvider
 *
 * @example
 * ```tsx
 * const { theme, toggleTheme } = useTheme()
 * ```
 */
export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext)
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
}

/**
 * useIsDarkTheme Hook
 *
 * Quick check if dark theme is active
 */
export function useIsDarkTheme(): boolean {
  return useTheme().isDark
}

/**
 * useIsLightTheme Hook
 *
 * Quick check if light theme is active
 */
export function useIsLightTheme(): boolean {
  return useTheme().isLight
}

/**
 * useThemeClass Hook
 *
 * Get CSS class for current theme
 *
 * @example
 * ```tsx
 * const themeClass = useThemeClass() // 'theme-dark' or 'theme-light'
 * <div className={themeClass}>Content</div>
 * ```
 */
export function useThemeClass(): string {
  const { theme } = useTheme()
  return `theme-${theme}`
}
